import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { publicClient } from "@/api/client";
import type { components } from "@/api/schema";
import { isDevelopment } from "@/utils/helpers";

import { Tenant } from "./types";

// These run outside the router, so no error boundary catches them; TenantGuard
// renders TenantNotFound itself.

/**
 * Returns a react-query useQuery result that identifies a tenant by domain name
 */
export function useIdentifyTenantByDomainNameQuery(
  domainName: string,
  options?: object,
): UseQueryResult<Tenant | null> {
  return useQuery<Tenant | null>({
    queryKey: ["tenancy", "identify-tenant", domainName],
    queryFn: () => {
      return identifyTenantByDomainName(domainName);
    },
    throwOnError: false,
    ...options,
  });
}

/**
 * Returns a react-query useQuery result that resolves to a list of all tenants
 */
export function useAllTenantsQuery(): UseQueryResult<Tenant[]> {
  return useQuery({
    queryKey: ["tenancy", "get-all-tenants"],
    queryFn: getAllTenants,
    // The endpoint is DEBUG-only; in production its 403 would reach the error boundary.
    enabled: isDevelopment(),
  });
}

export function useGetFirstTenantQuery(
  options?: object,
): UseQueryResult<Tenant> {
  return useQuery<Tenant>({
    queryKey: ["tenancy", "get-first-tenant"],
    queryFn: getFirstTenant,
    throwOnError: false,
    ...options,
  });
}

/**
 * Return the first tenant in the list of all tenants. For development, in case there is not tenant set
 */
export function getFirstTenant(): Promise<Tenant> {
  return getAllTenants().then((tenants) => tenants[0]);
}

type TenantRow = components["schemas"]["Tenant"];

const toTenant = (row: TenantRow): Tenant => ({
  identifier: row.schemaName,
  name: row.prettyName,
  domain: row.domainName,
});

/** The tenant serving `domainName`, or null when none does. */
async function identifyTenantByDomainName(
  domainName: string,
): Promise<Tenant | null> {
  const { data } = await publicClient.GET("/api/v1/tenants/", {
    params: { query: { domain: domainName } },
  });
  return data![0] ? toTenant(data![0]) : null;
}

/** Every tenant; the backend only lists them in DEBUG. */
async function getAllTenants(): Promise<Tenant[]> {
  const { data } = await publicClient.GET("/api/v1/tenants/");
  return data!.map(toTenant);
}
